import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ActivityInstance } from '@api';
import { useChildTokens } from './useChildTokens';
import { getActivityIcon } from './activityIcon';
import { getChildActivityStatus, ChildActivityStatus } from './activityStatus';
import { useRoutines } from '@/contexts/RoutineContext';
import { usePendingStarValues } from '@/components/activity/usePendingStarValues';
import {
  getCurrentStep,
  getCurrentStepIndex,
  getRoutineSteps,
  isLockedUntilItsDay,
  isRoutine,
} from '@/utils/routine';

/**
 * Barnvänligt aktivitetskort (prototypens .activity-item): emoji-ikon, namn,
 * klockslag/längd och en status-badge. Tap → öppnar aktiviteten
 * (/activity/:instanceId), där barnet kan starta timern.
 *
 * Status härleds på klienten (getChildActivityStatus) ur datum/flaggor — backend
 * skickar ingen status. Färger via barntemat (inga hårdkodade hex).
 */

type ChildActivityCardProps = {
  instance: ActivityInstance;
};

const badgeKey: Record<ChildActivityStatus, string> = {
  done: 'child.activity.status.done',
  ongoing: 'child.activity.status.ongoing',
  missed: 'child.activity.status.missed',
  upcoming: 'child.activity.status.upcoming',
};

const ChildActivityCard = ({ instance }: ChildActivityCardProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tokens = useChildTokens();
  const status = getChildActivityStatus(instance);
  const icon = getActivityIcon(instance);
  const { getRoutine } = useRoutines();
  // Barnet väljer själv vad aktiviteten ska vara värd, men värdet är ett FÖRSLAG tills
  // en vuxen har sagt sitt (V004) - och en aktivitet med ogodkänt värde ger noll
  // stjärnor när den klaras av. Det måste stå på kortet, annars gör barnet allt rätt
  // och undrar var stjärnan tog vägen. Timglaset bär beskedet för den som inte läser.
  const { isPending: isStarValuePending } = usePendingStarValues();
  const starValuePending = isStarValuePending(instance);

  // Rutin: kortet visar vad barnet ska göra JUST NU, inte hela kedjan. Steget hör bara
  // till en pågående rutin — före start finns inget aktuellt steg, och en klar rutin
  // har inget kvar. Kortet leder som vanligt in i aktiviteten, där Nästa-knappen bor.
  const routine = getRoutine(instance);
  const steps = isRoutine(routine) ? getRoutineSteps(routine) : [];
  const currentStep = status === 'ongoing' ? getCurrentStep(steps, instance.checkedSubtasks) : null;
  const stepNumber = getCurrentStepIndex(steps, instance.checkedSubtasks) + 1;

  // Låst till sin dag: hänglåset står redan på kortet, så barnet vet innan det trycker.
  // Kortet leder fortfarande in i aktiviteten - man ska få titta på morgondagen, bara
  // inte börja den, och förklaringen står där inne.
  const lockedUntilItsDay = isLockedUntilItsDay(routine, instance);

  const badgeStyles: Record<ChildActivityStatus, { bg: string; color: string }> = {
    done: { bg: tokens.greenLight, color: 'success.main' },
    ongoing: { bg: tokens.primaryLight, color: 'primary.main' },
    missed: { bg: tokens.attentionLight, color: tokens.attentionMain },
    upcoming: { bg: tokens.primaryLight, color: 'primary.main' },
  };

  const iconBg: Record<ChildActivityStatus, string> = {
    done: tokens.greenLight,
    ongoing: tokens.primaryLight,
    missed: tokens.attentionLight,
    upcoming: tokens.amberLight,
  };

  const time = format(new Date(instance.startDate), 'HH:mm');

  return (
    <Box
      component="button"
      type="button"
      onClick={() => navigate(`/activity/${instance.activityInstanceId}`)}
      aria-label={
        (lockedUntilItsDay ? `${t('routine.dayLockedTitle')}, ` : '') +
        (starValuePending ? `${t('starValue.pendingBadge')}, ` : '') +
        (currentStep
          ? `${instance.activityName}, ${currentStep.subtaskText}, ${t('routine.stepCounter', {
              current: stepNumber,
              total: steps.length,
            })}, ${t(badgeKey[status])}`
          : `${instance.activityName}, ${t(badgeKey[status])}`)
      }
      sx={{
        width: '100%',
        textAlign: 'left',
        bgcolor: tokens.card,
        border: '1px solid',
        borderColor: tokens.border,
        borderRadius: '14px',
        p: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        boxShadow: tokens.shadow,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'transform 0.15s, box-shadow 0.15s',
        '@media (hover: hover)': {
          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(45,49,66,0.1)' },
        },
        '&:active': { transform: 'scale(0.99)' },
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          display: 'grid',
          placeItems: 'center',
          fontSize: '1.4rem',
          flexShrink: 0,
          bgcolor: iconBg[status],
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: tokens.text }} noWrap>
          {instance.activityName}
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: tokens.textSoft, fontWeight: 600 }}>
          {lockedUntilItsDay && '🔒 '}⏱ {time} · {instance.duration}{' '}
          {t('child.activity.minutesShort')}
        </Typography>
        {currentStep && (
          <Typography sx={{ fontSize: '0.85rem', color: tokens.text, fontWeight: 800, mt: 0.25 }}>
            👉 {currentStep.subtaskText} ·{' '}
            {t('routine.stepCounter', { current: stepNumber, total: steps.length })}
          </Typography>
        )}
        {starValuePending && (
          <Typography
            sx={{ fontSize: '0.75rem', color: tokens.textSoft, fontWeight: 700, mt: 0.25 }}
          >
            ⏳ {t('starValue.pendingBadge')}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          px: 1.25,
          py: 0.5,
          borderRadius: '10px',
          fontSize: '0.75rem',
          fontWeight: 800,
          flexShrink: 0,
          bgcolor: badgeStyles[status].bg,
          color: badgeStyles[status].color,
        }}
      >
        {t(badgeKey[status])}
      </Box>
    </Box>
  );
};

export default ChildActivityCard;
